import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../Modal';
import { log } from '../../utils/logger';
import './SendData.css';

type SendDataType = 'text' | 'files';

interface PaymentOrder {
    amount: string;
    payerType: 'individual' | 'legal';
}

export const SendData = () => {
    const { isAuthenticated, login, logout } = useAuth();
    const navigate = useNavigate();
    const [endpointAddress, setEndpointAddress] = useState('');
    const [sendType, setSendType] = useState<SendDataType>('text');
    const [date, setDate] = useState('');
    const [cashAmount, setCashAmount] = useState('');
    const [cardAmount, setCardAmount] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [paymentOrders, setPaymentOrders] = useState<PaymentOrder[]>([{ amount: '', payerType: 'individual' }]);
    const [comment, setComment] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [sizeWarningModalOpen, setSizeWarningModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const checkStarted = useRef(false);

    useEffect(() => {
        fetch('/config.json')
            .then(res => res.json())
            .then(config => {
                if (config['endpoint-address']) setEndpointAddress(config['endpoint-address']);
                log('SendData', 'Загружен config', { hasEndpoint: !!config['endpoint-address'] });
            })
            .catch((err) => log('SendData', 'Ошибка загрузки config', err));
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            const id = localStorage.getItem('id');
            const token = localStorage.getItem('token');
            if (!id || !token) {
                logout();
                navigate('/', { replace: true });
            }
        }
    }, [isAuthenticated, logout, navigate]);

    useEffect(() => {
        if (isAuthenticated || !endpointAddress) return;
        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) {
            navigate('/', { replace: true });
            return;
        }
        if (checkStarted.current) return;
        checkStarted.current = true;
        let cancelled = false;
        const url = `${endpointAddress}/checkSession?id=${encodeURIComponent(id!)}&token=${encodeURIComponent(token!)}`;
        log('SendData', 'Проверка сессии', { url });
        fetch(url, { method: 'GET' })
            .then(res => res.json().catch(() => ({})))
            .then((data: { status?: unknown }) => {
                if (cancelled) return;
                const ok = data?.status === true || data?.status === 'true';
                log('SendData', 'Результат checkSession', { ok });
                if (ok) login();
                else navigate('/', { replace: true });
            })
            .catch((err) => {
                if (!cancelled) {
                    log('SendData', 'Ошибка checkSession', err);
                    checkStarted.current = false;
                    navigate('/', { replace: true });
                }
            });
        return () => { cancelled = true; };
    }, [isAuthenticated, login, endpointAddress, navigate]);

    const addPaymentOrder = () => {
        setPaymentOrders(prev => [...prev, { amount: '', payerType: 'individual' }]);
    };

    const removePaymentOrder = (index: number) => {
        if (paymentOrders.length <= 1) return;
        setPaymentOrders(prev => prev.filter((_, i) => i !== index));
    };

    const updatePaymentOrder = (index: number, field: keyof PaymentOrder, value: string) => {
        setPaymentOrders(prev =>
            prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
        );
    };

    const isValidNumber = (val: string): boolean => {
        if (!val.trim()) return false;
        const n = Number(val.trim().replace(',', '.'));
        return !isNaN(n) && n >= 0;
    };

    const validateTextForm = (): boolean => {
        const err: Record<string, string> = {};
        if (!date.trim()) err.date = 'Укажите дату';
        if (!cashAmount.trim()) err.cashAmount = 'Заполните поле';
        else if (!isValidNumber(cashAmount)) err.cashAmount = 'Некорректная сумма';
        if (!cardAmount.trim()) err.cardAmount = 'Заполните поле';
        else if (!isValidNumber(cardAmount)) err.cardAmount = 'Некорректная сумма';
        if (!transferAmount.trim()) err.transferAmount = 'Заполните поле';
        else if (!isValidNumber(transferAmount)) err.transferAmount = 'Некорректная сумма';
        const hasPaymentOrders = paymentOrders.some(p => p.amount.trim());
        if (!hasPaymentOrders) {
            err.paymentOrders = 'Укажите хотя бы одну сумму по платёжным поручениям';
        } else {
            paymentOrders.forEach((p, i) => {
                if (p.amount.trim() && !isValidNumber(p.amount)) err[`orderAmount_${i}`] = 'Некорректная сумма';
            });
        }
        setFieldErrors(err);
        return Object.keys(err).length === 0;
    };

    const validateFilesForm = (): boolean => {
        if (files.length === 0) {
            setFieldErrors({ files: 'Выберите хотя бы один файл или фото' });
            return false;
        }
        setFieldErrors({});
        return true;
    };

    const handleSubmit = async () => {
        log('SendData', 'Начало отправки данных', { type: sendType });
        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token || !endpointAddress) {
            log('SendData', 'Ошибка: нет авторизации или адреса сервера', { id: !!id, token: !!token, endpoint: !!endpointAddress });
            return;
        }

        if (sendType === 'text') {
            log('SendData', 'Отправка данных текстом');
            if (!validateTextForm()) return;
            setIsSubmitting(true);
            try {
                const paymentOrdersList = paymentOrders
                    .filter(p => p.amount.trim())
                    .map(p => ({
                        amount: p.amount.trim().replace(',', '.'),
                        payerType: p.payerType === 'legal' ? 'Юрлицо' : 'Физлицо',
                    }));
                const body = {
                    id,
                    token,
                    date: date.trim(),
                    cash: cashAmount.trim().replace(',', '.'),
                    card: cardAmount.trim().replace(',', '.'),
                    transaction: transferAmount.trim().replace(',', '.'),
                    'payment-orders': JSON.stringify(paymentOrdersList),
                    comment: comment.trim() ? comment.trim() : 'Ничего',
                };
                log('SendData', 'Тело запроса (текст)', {
                    date: body.date,
                    cash: body.cash,
                    card: body.card,
                    transaction: body.transaction,
                    paymentOrdersCount: paymentOrdersList.length,
                    comment: body.comment
                });
                const res = await fetch(`${endpointAddress}/sendDataText`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await res.json().catch(() => ({}));
                log('SendData', 'Ответ сервера (текст)', { status: res.status, ok: res.ok, data });
                if (res.ok && (data as any).status === '1') {
                    log('SendData', 'Данные успешно отправлены (текст)');
                    setSuccessModalOpen(true);
                } else if ((data as any).status === '3') {
                    alert((data as any).message || 'Подписка не активна. Отправка данных недоступна.');
                } else {
                    const errorText = JSON.stringify(data);
                    log('SendData', 'Ошибка при отправке текста', { status: res.status, response: errorText });
                }
            } catch (error) {
                log('SendData', 'Исключение при отправке текста', error);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            if (!validateFilesForm()) return;
            
            // Проверка размера файлов (максимум 2 МБ)
            const maxSizeBytes = 2 * 1024 * 1024; // 2 МБ в байтах
            const totalSize = files.reduce((sum, file) => sum + file.size, 0);
            const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
            
            log('SendData', 'Проверка размера файлов', { 
                totalSizeBytes: totalSize,
                totalSizeMB: totalSizeMB,
                maxSizeMB: '2',
                filesCount: files.length
            });
            
            if (totalSize > maxSizeBytes) {
                log('SendData', 'Превышен лимит размера файлов', { 
                    totalSizeMB, 
                    maxSizeMB: '2',
                    filesCount: files.length
                });
                setSizeWarningModalOpen(true);
                return;
            }
            
            setIsSubmitting(true);
            try {
                // Читаем файлы как data URL, затем отправляем base64 без префикса
                const dataUrls = await Promise.all(
                    files.map(
                        (file) =>
                            new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve((reader.result as string) ?? '');
                                reader.onerror = () => reject(reader.error);
                                reader.readAsDataURL(file);
                            })
                    )
                );
                const fileStrings = dataUrls.map((url) =>
                    url.includes(',') ? url.split(',')[1] : url
                );
                const body = {
                    id,
                    token,
                    file: fileStrings,
                    comment: comment.trim() ? comment.trim() : 'Ничего',
                };
                log('SendData', 'Отправка файлов', { 
                    count: fileStrings.length,
                    fileNames: files.map(f => f.name),
                    comment: body.comment
                });
                log('SendData', 'Тело запроса (файлы)', {
                    count: fileStrings.length,
                    file: fileStrings.map(f => f.substring(0, 100) + '...')
                });
                const res = await fetch(`${endpointAddress}/sendDataFile`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await res.json().catch(() => ({}));
                log('SendData', 'Ответ сервера (файлы)', { status: res.status, ok: res.ok, data });
                if (res.ok && (data as any).status === '1') {
                    log('SendData', 'Данные успешно отправлены (файлы)');
                    setSuccessModalOpen(true);
                } else if ((data as any).status === '3') {
                    alert((data as any).message || 'Подписка не активна. Отправка данных недоступна.');
                } else {
                    const errorText = JSON.stringify(data);
                    log('SendData', 'Ошибка при отправке файлов', { status: res.status, response: errorText });
                }
            } catch (error) {
                log('SendData', 'Исключение при отправке файлов', error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files ? Array.from(e.target.files) : [];
        log('SendData', 'Выбор файлов', { 
            selectedCount: selected.length,
            selectedNames: selected.map(f => f.name),
            currentCount: files.length
        });
        const maxFiles = 10;
        const currentCount = files.length;
        const newCount = currentCount + selected.length;
        
        if (newCount > maxFiles) {
            const allowed = maxFiles - currentCount;
            if (allowed > 0) {
                setFiles(prev => [...prev, ...selected.slice(0, allowed)]);
                log('SendData', `Ограничение файлов: добавлено ${allowed} из ${selected.length}`, { maxFiles, currentCount, newCount });
            } else {
                log('SendData', 'Ограничение файлов: превышен лимит', { maxFiles, currentCount, newCount });
            }
        } else {
            setFiles(prev => [...prev, ...selected]);
            log('SendData', 'Файлы добавлены', { totalCount: currentCount + selected.length });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSuccessClose = () => {
        setSuccessModalOpen(false);
        navigate('/');
    };

    return (
        <div className="senddata-page">
            <div className="senddata-container">
                <h1 className="senddata-title">Отправка данных о выручке</h1>

                <div className="senddata-info card-block">
                    <h2 className="senddata-subtitle">Обязательные данные</h2>
                    <p className="senddata-desc">
                        Данные должны содержать: дата, сумма наличной выручки, сумма по оплатам картами,
                        сумма по оплатам переводами, сумма по оплатам платёжными поручениями с указанием,
                        от кого поступила оплата (физлица или юрлица).
                    </p>
                    <p className="senddata-desc senddata-note">
                        Если вы присылаете выписки банка и отчёты о закрытии смены по кассе, в них вся эта
                        информация содержится и расшифровка не нужна.
                    </p>
                </div>

                <div className="senddata-type card-block">
                    <h2 className="senddata-subtitle">Способ отправки</h2>
                    <div className="senddata-type-tabs">
                        <button
                            type="button"
                            className={`senddata-tab ${sendType === 'text' ? 'active' : ''}`}
                            onClick={() => setSendType('text')}
                        >
                            Текстом
                        </button>
                        <button
                            type="button"
                            className={`senddata-tab ${sendType === 'files' ? 'active' : ''}`}
                            onClick={() => setSendType('files')}
                        >
                            Файлы (касса и банк)
                        </button>
                    </div>
                </div>

                {sendType === 'text' && (
                    <div className="senddata-form card-block">
                        <h2 className="senddata-subtitle">Данные текстом</h2>
                        <div className="form-row">
                            <label className="form-label">
                                Дата <span className="required">*</span>
                            </label>
                            <input
                                type="date"
                                className={`form-input ${fieldErrors.date ? 'error' : ''}`}
                                value={date}
                                onChange={e => setDate(e.target.value)}
                            />
                            {fieldErrors.date && <span className="form-error">{fieldErrors.date}</span>}
                        </div>
                        <div className="form-row">
                            <label className="form-label">Сумма наличной выручки (₽) <span className="required">*</span></label>
                            <input
                                type="text"
                                className={`form-input ${fieldErrors.cashAmount ? 'error' : ''}`}
                                placeholder="0"
                                value={cashAmount}
                                onChange={e => setCashAmount(e.target.value)}
                            />
                            {fieldErrors.cashAmount && <span className="form-error">{fieldErrors.cashAmount}</span>}
                        </div>
                        <div className="form-row">
                            <label className="form-label">Сумма по оплатам картами (₽) <span className="required">*</span></label>
                            <input
                                type="text"
                                className={`form-input ${fieldErrors.cardAmount ? 'error' : ''}`}
                                placeholder="0"
                                value={cardAmount}
                                onChange={e => setCardAmount(e.target.value)}
                            />
                            {fieldErrors.cardAmount && <span className="form-error">{fieldErrors.cardAmount}</span>}
                        </div>
                        <div className="form-row">
                            <label className="form-label">Сумма по оплатам переводами (₽) <span className="required">*</span></label>
                            <input
                                type="text"
                                className={`form-input ${fieldErrors.transferAmount ? 'error' : ''}`}
                                placeholder="0"
                                value={transferAmount}
                                onChange={e => setTransferAmount(e.target.value)}
                            />
                            {fieldErrors.transferAmount && <span className="form-error">{fieldErrors.transferAmount}</span>}
                        </div>

                        <div className="payment-orders-block">
                            <div className="form-row form-row-header">
                                <span className="form-label">Платёжные поручения <span className="required">*</span></span>
                                <button type="button" className="add-row-btn" onClick={addPaymentOrder}>
                                    + Добавить
                                </button>
                            </div>
                            {fieldErrors.paymentOrders && <span className="form-error">{fieldErrors.paymentOrders}</span>}
                            {paymentOrders.map((order, index) => (
                                <div key={index} className="payment-order-row">
                                    <input
                                        type="text"
                                        className={`form-input small ${fieldErrors[`orderAmount_${index}`] ? 'error' : ''}`}
                                        placeholder="Сумма (₽)"
                                        value={order.amount}
                                        onChange={e => updatePaymentOrder(index, 'amount', e.target.value)}
                                    />
                                    <select
                                        className="form-select"
                                        value={order.payerType}
                                        onChange={e => updatePaymentOrder(index, 'payerType', e.target.value as 'individual' | 'legal')}
                                    >
                                        <option value="individual">Физлицо</option>
                                        <option value="legal">Юрлицо</option>
                                    </select>
                                    <button
                                        type="button"
                                        className="remove-row-btn"
                                        onClick={() => removePaymentOrder(index)}
                                        disabled={paymentOrders.length <= 1}
                                        title="Удалить"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="form-row">
                            <label className="form-label">Комментарий (необязательно)</label>
                            <textarea
                                className="form-input form-textarea"
                                placeholder="Дополнительная информация"
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                )}

                {sendType === 'files' && (
                    <div className="senddata-files card-block">
                        <h2 className="senddata-subtitle">Файлы</h2>
                        <p className="senddata-desc muted">
                            Загрузите выписки банка и/или отчёты о закрытии смены по кассе. Можно загружать файлы любых форматов.
                        </p>
                        <label className="file-upload-zone">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                className="file-upload-input"
                            />
                            <div className="file-upload-placeholder">
                                <span className="file-upload-icon">📎</span>
                                <span>Нажмите или перетащите файлы сюда</span>
                            </div>
                        </label>
                        {fieldErrors.files && <span className="form-error">{fieldErrors.files}</span>}
                        {files.length > 0 && (
                            <ul className="file-list">
                                {files.map((f, i) => (
                                    <li key={i} className="file-list-item">
                                        <span className="file-list-name">{f.name}</span>
                                        <button type="button" className="file-list-remove" onClick={() => removeFile(i)}>
                                            Удалить
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div className="senddata-actions">
                    <button
                        type="button"
                        className="senddata-submit-btn"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Отправка...' : 'Отправить'}
                    </button>
                </div>
            </div>

            <Modal
                isOpen={successModalOpen}
                onClose={handleSuccessClose}
                title="Данные отправлены"
                type="success"
                buttonText="На главную"
            >
                <p>Данные о выручке успешно отправлены.</p>
            </Modal>

            <Modal
                isOpen={sizeWarningModalOpen}
                onClose={() => setSizeWarningModalOpen(false)}
                title="Превышен размер файлов"
                type="error"
                buttonText="Понятно"
            >
                <p>Общий размер файлов превышает 2 МБ. Пожалуйста, уменьшите размер файлов или удалите некоторые из них перед отправкой.</p>
            </Modal>

        </div>
    );
};
